import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, Circle, ArrowRight, EnvelopeSimple, CalendarBlank, Gavel } from '@phosphor-icons/react';
import { fetchOnboardingStatus } from '@/lib/meApi';
import { useAuthStore } from '@/features/auth/store';
import { canCreateMatter } from '@/lib/pupilUiBoundary';

// "I'll set up later" snoozes the panel for this long, per browser + user. It's a snooze, not a
// permanent dismiss: the panel's job is to keep guiding until setup is genuinely done, so it comes
// back the next day rather than letting a new barrister hide it once and never finish onboarding.
// (Completing setup still removes it permanently — that's driven by real state, not this key.)
const SNOOZE_MS = 24 * 60 * 60 * 1000;
const snoozeKey = (userId: string) => `setup-continuation-snooze:${userId}`;

/**
 * Dashboard onboarding continuation — keeps guiding a new barrister until their practice is set up,
 * then disappears entirely. Driven by REAL state (inbox connected · calendar syncing · first
 * matter), NOT a stored checklist, so it can never get out of sync and self-removes at 100%.
 * Renders nothing once complete (or while loading / on error) — no banner, no noise.
 * The barrister can also snooze it for 24h with "I'll set up later".
 */
export function SetupContinuation() {
  const userId = useAuthStore((s) => s.user?.id);
  const memberType = useAuthStore((s) => s.user?.memberType);
  const [snoozed, setSnoozed] = useState(() => {
    if (!userId) return false;
    const until = Number(localStorage.getItem(snoozeKey(userId)));
    return Number.isFinite(until) && until > Date.now();
  });

  const { data } = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: fetchOnboardingStatus,
    staleTime: 30_000,
  });

  if (!data || data.complete || snoozed) return null;

  const snoozeLater = () => {
    if (userId) localStorage.setItem(snoozeKey(userId), String(Date.now() + SNOOZE_MS));
    setSnoozed(true);
  };

  const steps = [
    {
      done: data.emailConnected,
      icon: <EnvelopeSimple className="h-4 w-4" />,
      title: 'Bring today’s instructions into Jurivoxa',
      action: 'Connect inbox',
      to: '/barrister/inbox',
    },
    {
      done: data.calendarConnected,
      icon: <CalendarBlank className="h-4 w-4" />,
      title: 'See your upcoming hearings automatically',
      action: 'Connect calendar',
      to: '/barrister/diary',
    },
    // "Add a matter" needs `case.create`, which a pupil does not hold — POST /matters answers
    // 403, so the step sent them to a form that could never be submitted. Pupils work on matters
    // they are assigned to; opening one is not their job. Omitted rather than shown-and-failed.
    ...(canCreateMatter(memberType)
      ? [{
          done: data.firstMatterCreated,
          icon: <Gavel className="h-4 w-4" />,
          title: 'Start your first instruction',
          action: 'Add a matter',
          to: '/barrister/matters/new',
        }]
      : []),
  ];
  const doneCount = steps.filter((s) => s.done).length;

  return (
    <div className="rounded-xl border border-brand-200/70 bg-brand-50/40 p-5">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-foreground">Finish setting up your practice</div>
          <div className="text-xs text-muted-foreground">
            A few minutes now and Jurivoxa runs your day for you.
          </div>
        </div>
        <span className="rounded-full bg-brand-100 px-2.5 py-1 text-xs font-semibold text-brand-700 dark:text-brand-300">
          {doneCount} of {steps.length} done
        </span>
      </div>

      <div className="space-y-1.5">
        {steps.map((s) => (
          <div
            key={s.to}
            className="flex items-center gap-3 rounded-lg border border-border/50 bg-card px-3.5 py-2.5"
          >
            {s.done ? (
              <CheckCircle className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" weight="fill" />
            ) : (
              <Circle className="h-5 w-5 shrink-0 text-muted-foreground/40" />
            )}
            <span className="shrink-0 text-muted-foreground">{s.icon}</span>
            <span className={`min-w-0 flex-1 text-sm ${s.done ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
              {s.title}
            </span>
            {!s.done && (
              <Link
                to={s.to}
                className="flex shrink-0 items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-brand-700 dark:text-brand-300 transition hover:bg-brand-50"
              >
                {s.action} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        ))}
      </div>

      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={snoozeLater}
          className="text-xs font-medium text-muted-foreground transition hover:text-foreground"
        >
          I’ll set up later
        </button>
      </div>
    </div>
  );
}
